import { Download, FileText, Loader2 } from 'lucide-react';
import { Suspense, lazy, useEffect, useState } from 'react';
import { isAudioPath } from '../../../server/src/audio-formats';
import type { Artifact } from '../lib/artifacts';
import { downloadWorkspaceFile, fetchPreviewBlob, previewMimeKind } from '../lib/files';

type Props = { artifact: Artifact };
const PdfDocumentPreview = lazy(() =>
  import('./PdfDocumentPreview').catch(() => ({
    default: ({ url }: { url: string; title: string }) => (
      <div role="alert" className="p-6 text-sm">
        Не удалось загрузить просмотрщик. Обновите страницу или скачайте исходный файл.
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 block text-primary underline"
        >
          Открыть PDF отдельно
        </a>
      </div>
    ),
  })),
);

/**
 * Visual preview for an artifact. HTML and SVG go into a sandboxed
 * iframe with `srcdoc`; workspace text renders as text, and PDF or
 * converted documents use the browser's document viewer. Inline
 * code artifacts remain in the Code tab.
 *
 * Sandbox: allow-scripts ONLY. Combining allow-scripts with
 * allow-same-origin on srcdoc gives the iframe the main app's
 * origin, from which its script can reach parent and exfiltrate
 * localStorage (JWT). Leaving allow-same-origin off forces the
 * iframe into an opaque origin — scripts still run but can't
 * touch the embedder.
 */
export function ArtifactPreview({ artifact }: Props) {
  if (artifact.kind === 'html') {
    return (
      <iframe
        title="Предпросмотр"
        srcDoc={artifact.body}
        sandbox="allow-scripts"
        className="h-full w-full border-0 bg-white"
      />
    );
  }

  if (artifact.kind === 'svg') {
    // SVG can be pasted into a neutral HTML shell so background +
    // sizing are predictable.
    const shell = `<!doctype html><html><head><style>
      html, body { margin: 0; padding: 16px; display: flex; align-items: center; justify-content: center; background: #fff; }
      svg { max-width: 100%; max-height: 100%; }
    </style></head><body>${artifact.body}</body></html>`;
    return (
      <iframe
        title="Предпросмотр"
        srcDoc={shell}
        sandbox="allow-same-origin"
        className="h-full w-full border-0 bg-white"
      />
    );
  }

  if (artifact.kind === 'binary' && artifact.relativePath && isAudioPath(artifact.relativePath)) {
    return <AudioPreview key={artifact.relativePath} artifact={artifact} />;
  }

  if (artifact.kind === 'binary') {
    return <BinaryPreview artifact={artifact} />;
  }

  return (
    <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
      Предпросмотр для <span className="mx-1 font-mono">{artifact.lang}</span> недоступен. Откройте
      вкладку «Код», чтобы прочитать содержимое.
    </div>
  );
}

function AudioPreview({ artifact }: { artifact: Artifact }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="flex h-full min-w-0 flex-col items-center justify-center gap-4 p-6">
      <p className="max-w-full break-words text-center text-sm">{artifact.title ?? 'Аудио'}</p>
      {/* biome-ignore lint/a11y/useMediaCaption: uploaded audio has no captions yet; sending it to chat produces a separate accessible TXT transcript. */}
      <audio
        controls
        preload="metadata"
        src={`/api/files/preview?path=${encodeURIComponent(artifact.relativePath ?? '')}`}
        aria-label={artifact.title ?? 'Аудиозапись'}
        className="max-w-full"
        onError={() => setFailed(true)}
      />
      <p className="text-center text-sm text-muted-foreground">
        После отправки в чат модель сохранит текстовую расшифровку.
      </p>
      {failed && (
        <p role="alert" className="text-center text-sm text-destructive">
          Не удалось воспроизвести аудио. Можно скачать исходный файл.
        </p>
      )}
      <a
        href={`/api/files/download?path=${encodeURIComponent(artifact.relativePath ?? '')}`}
        className="inline-flex min-h-11 items-center gap-2 rounded-md px-3 text-sm underline"
      >
        <Download size={16} aria-hidden="true" /> Скачать аудио
      </a>
    </div>
  );
}

function BinaryPreview({ artifact }: { artifact: Artifact }) {
  const [preview, setPreview] = useState<
    { kind: 'text'; text: string } | { kind: 'pdf' | 'html'; url: string } | null
  >(null);
  const [err, setErr] = useState<string | null>(null);
  const [dlBusy, setDlBusy] = useState(false);
  const path = artifact.relativePath;

  useEffect(() => {
    if (!path) return;
    let cancelled = false;
    const controller = new AbortController();
    let revokeUrl: string | null = null;
    setErr(null);
    setPreview(null);

    (async () => {
      try {
        const { blob, type } = await fetchPreviewBlob(path, controller.signal);
        if (cancelled) return;
        const kind = previewMimeKind(type);
        if (kind === 'text') {
          const content = await blob.text();
          if (!cancelled) setPreview({ kind, text: content });
          return;
        }
        if (!kind) throw new Error('Этот тип файла нельзя показать в предпросмотре');
        const obj = URL.createObjectURL(blob);
        revokeUrl = obj;
        setPreview({ kind, url: obj });
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Не удалось открыть предпросмотр');
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [path]);

  async function onDownload(): Promise<void> {
    if (!path) return;
    setDlBusy(true);
    try {
      await downloadWorkspaceFile(path);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось скачать файл');
    } finally {
      setDlBusy(false);
    }
  }

  // Errors from the preview endpoint — or no workspace path at all —
  // fall back to the "download card" UI.
  if (!path || err) {
    return <DownloadCard artifact={artifact} err={err} dlBusy={dlBusy} onDownload={onDownload} />;
  }

  if (!preview) {
    return (
      <output
        aria-live="polite"
        className="flex h-full items-center justify-center p-6 text-[13px] text-muted-foreground"
      >
        <Loader2 size={14} className="mr-2 animate-spin" />
        Готовим предпросмотр…
      </output>
    );
  }

  if (preview.kind === 'text') {
    return (
      <pre className="h-full overflow-auto whitespace-pre-wrap break-words p-4 font-mono text-[13px]">
        {preview.text}
      </pre>
    );
  }

  if (preview.kind === 'pdf') {
    return (
      <Suspense
        fallback={
          <output className="block p-6 text-sm text-muted-foreground">Загрузка PDF…</output>
        }
      >
        <PdfDocumentPreview
          key={preview.url}
          url={preview.url}
          title={artifact.title ?? 'Документ'}
        />
      </Suspense>
    );
  }

  return (
    <iframe
      title={`Предпросмотр: ${artifact.title ?? artifact.lang}`}
      src={preview.url}
      // Legacy HTML previews keep scripts disabled; Office now renders as PDF.
      sandbox="allow-same-origin allow-popups-to-escape-sandbox"
      className="h-full w-full border-0 bg-white"
    />
  );
}

function DownloadCard({
  artifact,
  err,
  dlBusy,
  onDownload,
}: {
  artifact: Artifact;
  err: string | null;
  dlBusy: boolean;
  onDownload: () => void | Promise<void>;
}) {
  const opener =
    artifact.lang === 'docx' || artifact.lang === 'doc'
      ? 'Word, Pages или LibreOffice Writer'
      : artifact.lang === 'pptx' || artifact.lang === 'ppt'
        ? 'PowerPoint, Keynote или LibreOffice Impress'
        : artifact.lang === 'xlsx' || artifact.lang === 'xls'
          ? 'Excel, Numbers или LibreOffice Calc'
          : artifact.lang === 'pdf'
            ? 'любой программе для PDF'
            : 'подходящем приложении';

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
      <div className="flex h-20 w-16 items-center justify-center rounded-lg border border-border bg-subtle text-muted-foreground">
        <FileText size={32} />
      </div>
      <div>
        <div className="text-[15px] font-medium">{artifact.title ?? 'Файл'}</div>
        <div className="mt-1 text-[12px] text-muted-foreground">
          <span className="font-mono uppercase">{artifact.lang}</span>
        </div>
      </div>
      <div className="max-w-sm text-[13px] text-muted-foreground">
        {err ?? `Скачайте файл и откройте его в: ${opener}.`}
      </div>
      {artifact.relativePath && (
        <button
          type="button"
          onClick={() => void onDownload()}
          disabled={dlBusy}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[13px] text-primary-foreground hover:opacity-90 disabled:opacity-60"
        >
          <Download size={14} />
          {dlBusy ? 'Скачивание…' : 'Скачать'}
        </button>
      )}
    </div>
  );
}
